import type { BillingPeriodResponse } from '@/api/main/types.gen'
import { toDayKey, todayKey } from './billingForecast'

const startOf = (period: BillingPeriodResponse) => toDayKey(period.dates.starting_from)
const endOf = (period: BillingPeriodResponse) => toDayKey(period.dates.ending_at)

export const sortPeriodsNewestFirst = (periods: BillingPeriodResponse[]): BillingPeriodResponse[] =>
  [...periods].sort((a, b) => startOf(b).localeCompare(startOf(a)) || b.id - a.id)

/**
 * The period to open by default: the one containing today (in the configured timezone),
 * otherwise the most recently started one, otherwise the soonest upcoming one.
 *
 * Period dates are plain calendar dates, so they are compared as `YYYY-MM-DD` strings
 * rather than converted to instants (which would shift them in negative-offset zones).
 */
export const pickDefaultBillingPeriod = (
  periods: BillingPeriodResponse[],
  now: Date,
  tz: string
): BillingPeriodResponse | null => {
  const today = todayKey(now, tz)
  const newestFirst = sortPeriodsNewestFirst(periods)
  return (
    newestFirst.find(period => startOf(period) <= today && today <= endOf(period)) ??
    newestFirst.find(period => startOf(period) <= today) ??
    newestFirst[newestFirst.length - 1] ??
    null
  )
}

/** `YYYY-MM-DD` → `MM.DD.YYYY`, the same in every locale. */
const formatDay = (dayKey: string) => {
  const [year, month, day] = dayKey.split('-')
  return `${month}.${day}.${year}`
}

export const formatBillingPeriodLabel = (period: BillingPeriodResponse): string =>
  `${formatDay(startOf(period))} – ${formatDay(endOf(period))}`
